import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Debt, DebtType } from '../../types';
import { getLocalDateString } from '../../lib/dateUtils';
import {
  HandCoins,
  Plus,
  Search,
  CheckCircle2,
  Phone,
  MessageCircle,
  Edit2,
  Trash2,
  X,
  Check,
  TrendingUp,
  TrendingDown,
  Scale,
  User,
  Sparkles,
} from 'lucide-react';

export const LoanManager: React.FC = () => {
  const { debts, addDebt, editDebt, settleDebt, deleteDebt, triggerConfetti, language } = useApp();
  const isBn = language === 'bn';

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'owed' | 'owe'>('all');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);

  // Form Fields
  const [personName, setPersonName] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<DebtType>('owed'); // 'owed' = পাবো / Receivable, 'owe' = পাবে / Payable
  const [date, setDate] = useState(() => getLocalDateString());
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');

  // Statistics Calculation
  const stats = useMemo(() => {
    let totalOwed = 0; // আমি পাবো (Receivable)
    let totalOwe = 0; // পাবে / দিতে হবে (Payable)
    let owedCount = 0;
    let oweCount = 0;
    let pendingCount = 0;
    let settledCount = 0;

    (debts || []).forEach((d) => {
      const amt = Number(d.amount) || 0;
      if (d.type === 'owed') {
        owedCount++;
      } else {
        oweCount++;
      }

      if (d.status === 'settled') {
        settledCount++;
      } else {
        pendingCount++;
        if (d.type === 'owed') {
          totalOwed += amt;
        } else {
          totalOwe += amt;
        }
      }
    });

    const netBalance = totalOwed - totalOwe;

    return {
      totalOwed,
      totalOwe,
      netBalance,
      owedCount,
      oweCount,
      pendingCount,
      settledCount,
      totalCount: (debts || []).length,
    };
  }, [debts]);

  // Filtered list
  const filteredDebts = useMemo(() => {
    return (debts || []).filter((d) => {
      // Filter Type
      if (filterType === 'owed' && d.type !== 'owed') return false;
      if (filterType === 'owe' && d.type !== 'owe') return false;

      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = d.personName.toLowerCase().includes(q);
        const matchPhone = d.phone && d.phone.toLowerCase().includes(q);
        const matchNote = d.note && d.note.toLowerCase().includes(q);
        return matchName || matchPhone || matchNote;
      }

      return true;
    });
  }, [debts, filterType, searchQuery]);

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingDebt(null);
    setPersonName('');
    setAmount('');
    setType('owed');
    setDate(getLocalDateString());
    setPhone('');
    setNote('');
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (debt: Debt) => {
    setEditingDebt(debt);
    setPersonName(debt.personName);
    setAmount(String(debt.amount));
    setType(debt.type);
    setDate(debt.date || debt.createdAt || getLocalDateString());
    setPhone(debt.phone || '');
    setNote(debt.note || '');
    setIsModalOpen(true);
  };

  // Handle Save
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!personName.trim() || !amount) return;

    if (editingDebt) {
      editDebt(editingDebt.id, {
        personName: personName.trim(),
        amount: Number(amount) || 0,
        type,
        date: editingDebt.date || date,
        dueDate: undefined,
        phone: phone.trim() || undefined,
        note: note.trim() || undefined,
      });
    } else {
      addDebt({
        personName: personName.trim(),
        amount: Number(amount) || 0,
        type,
        date: getLocalDateString(),
        dueDate: undefined,
        phone: phone.trim() || undefined,
        note: note.trim() || undefined,
      });
      triggerConfetti();
    }

    setIsModalOpen(false);
    setEditingDebt(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top 4 Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Owed (পাবো / Receivable) */}
        <div className="p-5 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-teal-500/10 border border-emerald-500/20 dark:border-emerald-500/30 rounded-3xl relative overflow-hidden shadow-xs">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
              {isBn ? 'মোট পাবো' : 'Total Receivable'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono text-emerald-600 dark:text-emerald-400">
            ৳ {stats.totalOwed.toLocaleString('en-US')}
          </div>
          <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80 mt-1 font-medium">
            {isBn ? 'অন্যের কাছে মোট পাওনা টাকা' : 'Total money owed to you by others'}
          </p>
        </div>

        {/* Total Owe (পাবে / Payable) */}
        <div className="p-5 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-pink-500/10 border border-rose-500/20 dark:border-rose-500/30 rounded-3xl relative overflow-hidden shadow-xs">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-bold text-rose-700 dark:text-rose-300">
              {isBn ? 'মোট পাবে' : 'Total Payable'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center">
              <TrendingDown className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono text-rose-600 dark:text-rose-400">
            ৳ {stats.totalOwe.toLocaleString('en-US')}
          </div>
          <p className="text-[11px] text-rose-600/80 dark:text-rose-400/80 mt-1 font-medium">
            {isBn ? 'অন্যকে যে টাকা ফেরত দিতে হবে' : 'Total money you owe to others'}
          </p>
        </div>

        {/* Net Balance */}
        <div className="p-5 bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-cyan-500/10 border border-blue-500/20 dark:border-blue-500/30 rounded-3xl relative overflow-hidden shadow-xs">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
              {isBn ? 'নেট স্থিতি (Net Balance)' : 'Net Balance'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Scale className="w-4 h-4" />
            </div>
          </div>
          <div
            className={`text-2xl sm:text-3xl font-black font-mono ${
              stats.netBalance >= 0
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-rose-600 dark:text-rose-400'
            }`}
          >
            ৳ {stats.netBalance >= 0 ? '+' : ''}
            {stats.netBalance.toLocaleString('en-US')}
          </div>
          <p className="text-[11px] text-blue-600/80 dark:text-blue-400/80 mt-1 font-medium">
            {stats.netBalance >= 0
              ? isBn
                ? 'পাওনা দেনার চেয়ে বেশি'
                : 'Receivables exceed payables'
              : isBn
              ? 'দেনা পাওনার চেয়ে বেশি'
              : 'Payables exceed receivables'}
          </p>
        </div>

        {/* Settled / Summary */}
        <div className="p-5 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-orange-500/10 border border-amber-500/20 dark:border-amber-500/30 rounded-3xl relative overflow-hidden shadow-xs">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-bold text-amber-700 dark:text-amber-300">
              {isBn ? 'পরিশোধিত ও মোট হিসাব' : 'Settled & Summary'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono text-amber-600 dark:text-amber-400">
            {stats.settledCount}{' '}
            <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
              / {stats.totalCount} {isBn ? 'টি' : 'items'}
            </span>
          </div>
          <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80 mt-1 font-medium">
            {isBn
              ? `${stats.pendingCount} টি এন্ট্রি বর্তমানে চলমান রয়েছে`
              : `${stats.pendingCount} active entries currently open`}
          </p>
        </div>
      </div>

      {/* Action Ribbon & Filters */}
      <div className="p-4 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
              filterType === 'all'
                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {isBn ? `সকল (${stats.totalCount})` : `All (${stats.totalCount})`}
          </button>

          <button
            onClick={() => setFilterType('owed')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
              filterType === 'owed'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span>{isBn ? `পাবো (${stats.owedCount})` : `Receivable (${stats.owedCount})`}</span>
          </button>

          <button
            onClick={() => setFilterType('owe')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
              filterType === 'owe'
                ? 'bg-rose-600 text-white shadow-xs'
                : 'bg-rose-500/10 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-rose-500" />
            <span>{isBn ? `পাবে (${stats.oweCount})` : `Payable (${stats.oweCount})`}</span>
          </button>
        </div>

        {/* Search & Add Button */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-56">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={isBn ? 'নাম, মোবাইল বা বিবরণ খুঁজুন...' : 'Search name, phone or note...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-2xl text-xs font-black shadow-md shadow-amber-500/25 flex items-center gap-1.5 transition-all transform active:scale-95 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span>{isBn ? 'নতুন এন্ট্রি যোগ' : 'Add New Entry'}</span>
          </button>
        </div>
      </div>

      {/* Loan Cards Grid */}
      {filteredDebts.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl">
          <div className="w-16 h-16 rounded-3xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto mb-3">
            <HandCoins className="w-8 h-8" />
          </div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
            {isBn ? 'কোনো হিসাব পাওয়া যায়নি' : 'No entries found'}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
            {isBn
              ? 'পাবো বা পাবে হিসাব রাখতে "+ নতুন এন্ট্রি যোগ" বাটনে ক্লিক করুন।'
              : 'Click "+ Add New Entry" to track money you will get or owe.'}
          </p>
          <button
            onClick={handleOpenCreate}
            className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{isBn ? 'প্রথম এন্ট্রি যোগ করুন' : 'Add First Entry'}</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredDebts.map((loan) => {
            const isOwed = loan.type === 'owed'; // পাবো / Receivable
            const isSettled = loan.status === 'settled';

            // Clean phone for WhatsApp / Tel
            const rawPhone = loan.phone ? loan.phone.replace(/[^0-9+]/g, '') : '';
            const waPhone = rawPhone.startsWith('0') ? `88${rawPhone}` : rawPhone;

            return (
              <div
                key={loan.id}
                className={`p-5 bg-white dark:bg-[#111827] border rounded-3xl shadow-xs transition-all hover:shadow-md flex flex-col justify-between relative overflow-hidden group ${
                  isSettled
                    ? 'border-slate-200/80 dark:border-slate-800 opacity-75'
                    : isOwed
                    ? 'border-emerald-200/80 dark:border-emerald-900/40 hover:border-emerald-300'
                    : 'border-rose-200/80 dark:border-rose-900/40 hover:border-rose-300'
                }`}
              >
                {/* Header Strip Indicator */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1.5 ${
                    isSettled
                      ? 'bg-slate-300 dark:bg-slate-700'
                      : isOwed
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
                      : 'bg-gradient-to-r from-rose-500 to-pink-500'
                  }`}
                />

                <div>
                  {/* Top Badges & Actions */}
                  <div className="flex items-start justify-between gap-2 mb-3 pt-1">
                    <div className="flex items-center gap-2">
                      <div
                        className={`px-2.5 py-1 rounded-xl text-[11px] font-black flex items-center gap-1 ${
                          isSettled
                            ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                            : isOwed
                            ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                            : 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isSettled
                              ? 'bg-slate-400'
                              : isOwed
                              ? 'bg-emerald-500'
                              : 'bg-rose-500'
                          }`}
                        />
                        <span>{isOwed ? (isBn ? 'পাবো' : 'Receivable') : (isBn ? 'পাবে' : 'Payable')}</span>
                      </div>

                      {isSettled && (
                        <span className="px-2 py-0.5 rounded-lg bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 text-[10px] font-bold border border-teal-200 dark:border-teal-800 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-teal-500" />
                          <span>{isBn ? 'পরিশোধিত' : 'Settled'}</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleOpenEdit(loan)}
                        className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                        title={isBn ? 'এডিট করুন' : 'Edit'}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteDebt(loan.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition-colors"
                        title={isBn ? 'মুছে ফেলুন' : 'Delete'}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Person Name & Amount */}
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 flex items-center justify-center font-black text-xs">
                        <User className="w-4 h-4" />
                      </div>
                      <h4 className="text-base font-black text-slate-900 dark:text-white truncate">
                        {loan.personName}
                      </h4>
                    </div>

                    <div
                      className={`text-xl font-black font-mono tracking-tight ${
                        isSettled
                          ? 'text-slate-400 line-through'
                          : isOwed
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-rose-600 dark:text-rose-400'
                      }`}
                    >
                      ৳ {Number(loan.amount || 0).toLocaleString('en-US')}
                    </div>
                  </div>

                  {/* Phone & Contact Buttons */}
                  {loan.phone && (
                    <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/80">
                      <span className="text-xs text-slate-600 dark:text-slate-400 font-mono flex items-center gap-1">
                        <Phone className="w-3 h-3 text-slate-400" />
                        <span>{loan.phone}</span>
                      </span>

                      <div className="flex items-center gap-1.5 ml-auto">
                        <a
                          href={`tel:${rawPhone}`}
                          className="px-2 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 text-blue-600 dark:text-blue-400 text-[11px] font-bold flex items-center gap-1 transition-colors"
                          title={isBn ? 'কল করুন' : 'Call'}
                        >
                          <Phone className="w-2.5 h-2.5" />
                          <span>Call</span>
                        </a>

                        <a
                          href={`https://wa.me/${waPhone}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold flex items-center gap-1 transition-colors"
                          title={isBn ? 'WhatsApp মেসেজ দিন' : 'Send WhatsApp'}
                        >
                          <MessageCircle className="w-2.5 h-2.5" />
                          <span>WhatsApp</span>
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Notes / Details */}
                  {loan.note && (
                    <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      <p className="text-[11px] bg-slate-50 dark:bg-slate-900/80 p-2 rounded-xl text-slate-700 dark:text-slate-300 italic border border-slate-100 dark:border-slate-800">
                        "{loan.note}"
                      </p>
                    </div>
                  )}
                </div>

                {/* Settle Action Button */}
                {!isSettled && (
                  <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button
                      onClick={() => settleDebt(loan.id)}
                      className="w-full py-2 bg-slate-100 dark:bg-slate-800 hover:bg-teal-500 hover:text-white dark:hover:bg-teal-600 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 active:scale-95 group-hover:border-teal-500"
                    >
                      <Check className="w-3.5 h-3.5 text-teal-500 group-hover:text-white transition-colors" />
                      <span>{isBn ? 'পরিশোধ হিসেবে চিহ্নিত করুন' : 'Mark as Settled'}</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Loan Modal */}
      {isModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <HandCoins className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {editingDebt
                    ? isBn
                      ? 'এন্ট্রি এডিট করুন'
                      : 'Edit Entry'
                    : isBn
                    ? 'নতুন এন্ট্রি যোগ করুন'
                    : 'Add New Entry'}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {/* Type Switcher (পাবো vs পাবে) */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
                  {isBn ? 'ধরন নির্বাচন করুন' : 'Select Type'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setType('owed')}
                    className={`py-2.5 px-3 rounded-2xl text-xs font-black flex items-center justify-center gap-2 border transition-all ${
                      type === 'owed'
                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/25'
                        : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-white" />
                    <span>{isBn ? 'পাবো' : 'Receivable (I will get)'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setType('owe')}
                    className={`py-2.5 px-3 rounded-2xl text-xs font-black flex items-center justify-center gap-2 border transition-all ${
                      type === 'owe'
                        ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-500/25'
                        : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-white" />
                    <span>{isBn ? 'পাবে' : 'Payable (I owe)'}</span>
                  </button>
                </div>
              </div>

              {/* Person Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  {isBn ? 'নাম (Name)' : 'Name'} <span className="text-rose-500 font-bold">*</span>
                </label>
                <input
                  type="text"
                  placeholder={isBn ? 'ব্যক্তির নাম লিখুন...' : "Enter person's name..."}
                  value={personName}
                  onChange={(e) => setPersonName(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  {isBn ? 'টাকা (Amount ৳)' : 'Amount (৳)'} <span className="text-rose-500 font-bold">*</span>
                </label>
                <input
                  type="number"
                  placeholder={isBn ? 'টাকার পরিমাণ লিখুন' : 'Enter amount'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  min="1"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-mono font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Mobile Phone (Optional) */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  {isBn ? 'মোবাইল নম্বর' : 'Mobile Number'}{' '}
                  <span className="text-slate-400 font-normal">
                    ({isBn ? 'ঐচ্ছিক' : 'Optional'})
                  </span>
                </label>
                <input
                  type="tel"
                  placeholder={isBn ? 'যেমন: 017XXXXXXXX' : 'e.g. 017XXXXXXXX'}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-mono text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Comments / Note (Optional) */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  {isBn ? 'মন্তব্য / নোট' : 'Comments / Note'}{' '}
                  <span className="text-slate-400 font-normal">
                    ({isBn ? 'ঐচ্ছিক' : 'Optional'})
                  </span>
                </label>
                <textarea
                  rows={2}
                  placeholder={isBn ? 'মন্তব্য বা অতিরিক্ত বিবরণ...' : 'Notes or extra details...'}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  {isBn ? 'বাতিল' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-md shadow-amber-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>
                    {editingDebt
                      ? isBn
                        ? 'আপডেট করুন'
                        : 'Update Entry'
                      : isBn
                      ? 'সেভ করুন'
                      : 'Save Entry'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
